import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { MultiTextField } from '@/components/form/fields/text/multi/MultiTextInput';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useCompany, useCompanyUsers } from '@/services/api/hooks/companies';
import { useInvitesToCompany } from '@/services/api/hooks/invites';
import { zodResolver } from '@hookform/resolvers/zod';

import { FunctionComponent, Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const InviteToCustomerFormFields = z.object({
  emails: z.array(z.string())
});

type InviteToCustomerFormFields = z.infer<typeof InviteToCustomerFormFields>;

const InviteUsersToCustomerModal: FunctionComponent<ModalProps<{ mspId: string; customerId: string }>> = props => {
  const InviteUsersToMsp = () => {
    const mspId = props.props?.mspId;
    if (!mspId) throw new Error('Missing partner id');
    const customerId = props.props?.customerId;
    if (!customerId) throw new Error('Missing company id');

    const [company] = useCompany(mspId, customerId);
    const [users] = useCompanyUsers(mspId, customerId);
    const signatory = users.items.find(user => user.id == company.signatoryId);

    let domain: string = '';
    if (signatory?.email || users.items.length > 0) {
      const emailParts = (signatory?.email || users.items[0].email).split('@');
      if (typeof emailParts === 'string' || emailParts.length !== 2) throw new Error('Invalid email');
      domain = emailParts[1];
    }

    const [domainError, setDomainError] = useState<string | undefined>(undefined);

    const invitesToCustomer = useInvitesToCompany();
    const methods = useForm<InviteToCustomerFormFields>({
      resolver: zodResolver(InviteToCustomerFormFields)
    });

    const onSubmit = async (data: InviteToCustomerFormFields) => {
      if (domain && data.emails.some(email => !email.endsWith(domain))) {
        setDomainError('You can only invite users with the same email domain.');
        return;
      }

      const results = await invitesToCustomer.mutateAsync({
        partnerId: props.props!.mspId,
        companyId: props.props!.customerId,
        emails: data.emails
      });

      if (Array.isArray(results) && results.length) {
        props.close();
      }
    };

    const errorObj = invitesToCustomer.error as Error | undefined;
    const error = errorObj ? errorObj.message : domainError ?? '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Invite Customer</h1>
        </Modal.Controls>

        <Form className="flex w-full flex-col space-y-4 py-3" methods={methods} onSubmit={onSubmit}>
          <MultiTextField label="Email addresses" id="emails" />

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex justify-end pt-2">
            <Button
              loading={invitesToCustomer.isLoading}
              className="w-fit"
              variant={ButtonVariant.Primary}
              type="submit"
            >
              Invite
            </Button>
          </div>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <InviteUsersToMsp />
      </Suspense>
    </Modal>
  );
};

export default InviteUsersToCustomerModal;
